
import { supabase } from "@/integrations/supabase/client";

// Services API
export interface Service {
  id: string;
  title: string;
  description: string;
  icon_name: string;
  hero_image_url?: string;
  video_url?: string;
  details?: string;
  features?: string[];
  slug?: string;
  process_steps?: Array<{ title: string; description: string }>;
  title_i18n?: Record<string, string>;
  description_i18n?: Record<string, string>;
  details_i18n?: Record<string, string>;
  features_i18n?: Record<string, string[]>;
  process_steps_i18n?: Record<string, Array<{ title: string; description: string }>>;
  display_order?: number;
  created_at?: string;
  updated_at?: string;
}

export async function getServiceBySlug(slug: string) {
  const { data, error } = await supabase
    .from("services")
    .select("*")
    .eq("slug", slug)
    .single();
  
  if (error) {
    console.error("Error fetching service:", error);
    throw error;
  }
  
  return data as Service;
}

export async function getServices() {
  const { data, error } = await supabase.from("services").select("*").order("display_order", { ascending: true });
  
  if (error) {
    console.error("Error fetching services:", error);
    throw error;
  }
  
  return data as Service[];
}

export async function createService(service: Omit<Service, "id" | "created_at" | "updated_at">) {
  const { data, error } = await supabase.from("services").insert(service).select().single();
  
  if (error) {
    console.error("Error creating service:", error);
    throw error;
  }
  
  return data as Service;
}

export async function updateService(id: string, service: Partial<Omit<Service, "id" | "created_at" | "updated_at">>) {
  const { data, error } = await supabase.from("services").update(service).eq("id", id).select().single();
  
  if (error) {
    console.error("Error updating service:", error);
    throw error;
  }
  
  return data as Service;
}

export async function deleteService(id: string) {
  const { error } = await supabase.from("services").delete().eq("id", id);
  
  if (error) {
    console.error("Error deleting service:", error);
    throw error;
  }
  
  return true;
}

// Jobs API
export interface Job {
  id: string;
  title: string;
  description: string;
  requirements: string;
  location: string;
  salary?: string;
  job_type?: string;
  is_active: boolean;
  display_order?: number;
  created_at?: string;
  updated_at?: string;
}

export async function getJobs() {
  const { data, error } = await supabase.from("jobs").select("*").order("display_order", { ascending: true });
  
  if (error) {
    console.error("Error fetching jobs:", error);
    throw error;
  }
  
  return data as Job[];
}

export async function createJob(job: Omit<Job, "id" | "created_at" | "updated_at">) {
  const { data, error } = await supabase.from("jobs").insert(job).select().single();
  
  if (error) {
    console.error("Error creating job:", error);
    throw error;
  }
  
  return data as Job;
}

export async function updateJob(id: string, job: Partial<Omit<Job, "id" | "created_at" | "updated_at">>) {
  const { data, error } = await supabase.from("jobs").update(job).eq("id", id).select().single();
  
  if (error) {
    console.error("Error updating job:", error);
    throw error;
  }
  
  return data as Job;
}

export async function deleteJob(id: string) {
  const { error } = await supabase.from("jobs").delete().eq("id", id);
  
  if (error) {
    console.error("Error deleting job:", error);
    throw error;
  }
  
  return true;
}

// Portfolio API
export interface PortfolioItem {
  id: string;
  title: string;
  description: string;
  image_url: string;
  logo_url?: string;
  project_url?: string;
  categories: string[];
  title_i18n?: { en: string; ar: string; de: string; tr: string; ku: string };
  description_i18n?: { en: string; ar: string; de: string; tr: string; ku: string };
  is_active: boolean;
  display_order?: number;
  created_at?: string;
  updated_at?: string;
}

export async function getPortfolioItems() {
  const { data, error } = await supabase.from("portfolio_items").select("*").order("display_order", { ascending: true });
  
  if (error) {
    console.error("Error fetching portfolio items:", error);
    throw error;
  }
  
  return data as PortfolioItem[];
}

/** Public, auth-independent portfolio feed: only active items, same for every visitor. */
export async function getPublicPortfolioItems() {
  const { data, error } = await supabase
    .from("portfolio_items")
    .select("*")
    .eq("is_active", true)
    .order("display_order", { ascending: true });
  if (error) throw error;
  return data as PortfolioItem[];
}

export async function createPortfolioItem(item: Omit<PortfolioItem, "id" | "created_at" | "updated_at">) {
  const { data, error } = await supabase.from("portfolio_items").insert(item).select().single();
  
  if (error) {
    console.error("Error creating portfolio item:", error);
    throw error;
  }
  
  return data as PortfolioItem;
}

export async function updatePortfolioItem(id: string, item: Partial<Omit<PortfolioItem, "id" | "created_at" | "updated_at">>) {
  const { data, error } = await supabase.from("portfolio_items").update(item).eq("id", id).select().single();
  
  if (error) {
    console.error("Error updating portfolio item:", error);
    throw error;
  }
  
  return data as PortfolioItem;
}

export async function deletePortfolioItem(id: string) {
  const { error } = await supabase.from("portfolio_items").delete().eq("id", id);
  
  if (error) {
    console.error("Error deleting portfolio item:", error);
    throw error;
  }
  
  return true;
}
